/**
 * 时间格式化工具
 * 用于将服务器时间转换为用户本地时间显示
 */

/**
 * 获取安全的语言标签
 */
const getSafeLocale = () => {
  try {
    const lang = navigator.language || 'zh-CN';
    // 移除 @posix 等无效后缀
    const cleanLang = lang.split('@')[0];
    // 验证语言标签是否有效
    new Date().toLocaleString(cleanLang);
    return cleanLang;
  } catch (e) {
    return 'zh-CN';
  }
};

/**
 * 格式化日期时间为用户本地时间
 * @param {string} dateString - ISO格式的日期字符串
 * @returns {string} 格式化后的本地时间字符串
 */
export const formatLocalDateTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    // 如果时间字符串没有时区信息，假定为UTC时间
    let date;
    if (dateString.endsWith('Z') || dateString.includes('+') || /T\d{2}:\d{2}:\d{2}.*-\d{2}/.test(dateString)) {
      date = new Date(dateString);
    } else {
      // 服务器返回的是UTC时间，添加Z后缀
      date = new Date(dateString + 'Z');
    }
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    // 使用本地时间格式化为 yyyy/MM/dd HH:mm 格式
    const pad = (n) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  } catch (e) {
    console.error('Date formatting error:', e);
    return dateString;
  }
};

/**
 * 格式化为相对时间（如"5分钟前"）
 * @param {string} dateString - ISO格式的日期字符串
 * @param {object} t - i18n翻译函数
 * @returns {string} 相对时间字符串
 */
export const formatRelativeTime = (dateString, t) => {
  if (!dateString) return '';
  
  try {
    let date;
    if (dateString.endsWith('Z') || dateString.includes('+') || /T\d{2}:\d{2}:\d{2}.*-\d{2}/.test(dateString)) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString + 'Z');
    }
    
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    // 使用翻译函数，如果没有则使用默认中文
    const getText = (key, fallback) => {
      if (t && typeof t === 'function') {
        const translated = t(key);
        return translated !== key ? translated : fallback;
      }
      return fallback;
    };
    
    if (diffSec < 60) {
      return getText('time.justNow', '刚刚');
    } else if (diffMin < 60) {
      return `${diffMin} ${getText('time.minutesAgo', '分钟前')}`;
    } else if (diffHour < 24) {
      return `${diffHour} ${getText('time.hoursAgo', '小时前')}`;
    } else if (diffDay < 7) {
      return `${diffDay} ${getText('time.daysAgo', '天前')}`;
    } else {
      // 超过7天显示具体日期
      return formatLocalDateTime(dateString);
    }
  } catch (e) {
    console.error('Relative time formatting error:', e);
    return dateString;
  }
};

export default { formatLocalDateTime, formatRelativeTime };
